import type { QueryKey } from "@tanstack/react-query";
import { type ApiErrorPayload, extractApiError } from "@/utils/apiErrorMapper";

export type MutationReplayHandler = (payload: unknown) => Promise<unknown>;
export type MutationInvalidate = (payload: unknown) => QueryKey[];
export type MutationReplaySuccess = (payload: unknown, data: unknown) => void;

interface RegistryEntry {
	handler: MutationReplayHandler;
	payloadVersion: number;
	/** Registered success effect — query keys to invalidate after a replay. */
	invalidate?: MutationInvalidate;
	/** Registered replay-only success effect (see `replayMutation`). */
	onReplaySuccess?: MutationReplaySuccess;
}

const registry = new Map<string, RegistryEntry>();

export function registerMutation(
	key: string,
	handler: MutationReplayHandler,
	payloadVersion: number,
	invalidate?: MutationInvalidate,
	onReplaySuccess?: MutationReplaySuccess,
): void {
	registry.set(key, { handler, payloadVersion, invalidate, onReplaySuccess });
}

/**
 * Query keys the registered `invalidate` effect declares for a payload, or `[]`
 * when the key is unknown or declares no effect. Path-agnostic so the replay
 * path (which never runs the per-component `onSuccess`) can still refresh lists.
 */
export function getInvalidationKeys(key: string, payload: unknown): QueryKey[] {
	return registry.get(key)?.invalidate?.(payload) ?? [];
}

/**
 * Structured replay result, total over every failure. Persisted (minus the `ok`
 * arm) as `QueueItem.lastError` and resolved to a localized string at render
 * time — so a replay failure reads as well as the equivalent online failure.
 */
export type ReplayOutcome =
	| { type: "ok"; data: unknown }
	// `extractApiError(err)` returned a payload — a real server rejection.
	| { type: "server-error"; apiError: ApiErrorPayload }
	// No response at all — still unreachable / connection dropped mid-flight.
	| { type: "network-error" }
	| { type: "version-mismatch" }
	| { type: "unknown-key" };

/** The failure arms — the shape persisted and fed to the render-time resolver. */
export type ReplayFailure = Exclude<ReplayOutcome, { type: "ok" }>;

/**
 * Replay a single queued item. Rejects version mismatches to prevent data
 * corruption.
 *
 * Only the queue path calls this (`useOfflineQueue.replayItem`) — the online
 * path calls the raw handler through `defineOfflineMutation`'s `mutationFn`.
 * That asymmetry is what makes the registered `onReplaySuccess` effect
 * replay-only, so a callsite that already reports its own outcome online can
 * add a replay-time disclosure without double-reporting.
 */
export async function replayMutation(
	key: string,
	payload: unknown,
	payloadVersion: number,
): Promise<ReplayOutcome> {
	const entry = registry.get(key);
	if (!entry) {
		// Dev detail stays for support; it never reaches the user (D3).
		console.warn(`[offline-replay] No handler registered for "${key}"`);
		return { type: "unknown-key" };
	}
	if (entry.payloadVersion !== payloadVersion) {
		console.warn(
			`[offline-replay] Handler version ${entry.payloadVersion} ≠ payload version ${payloadVersion} for "${key}"`,
		);
		return { type: "version-mismatch" };
	}
	let data: unknown;
	try {
		data = await entry.handler(payload);
	} catch (err) {
		const apiError = extractApiError(err);
		// A payload means the server responded and rejected; its absence means we
		// never reached the server (still offline) — a distinct, common path.
		return apiError
			? { type: "server-error", apiError }
			: { type: "network-error" };
	}
	// Guarded on purpose: the write is already accepted at this point, so a
	// throwing disclosure effect must never turn it into a retry (the batch
	// endpoint is not idempotent — a retry would duplicate accepted rows).
	try {
		entry.onReplaySuccess?.(payload, data);
	} catch (err) {
		console.warn(`[offline-replay] onReplaySuccess threw for "${key}"`, err);
	}
	return { type: "ok", data };
}
